import React, { useEffect, useState, useRef } from "react";
import { handleTokenAndPostRequest } from "../utils/utils";
import Spinner from "react-bootstrap/Spinner";
import {
  FaPlus, FaMagic, FaPaperPlane, FaEnvelope, FaEye,
  FaBroadcastTower, FaCheckCircle, FaTimes, FaArrowLeft,
  FaTrash, FaNewspaper, FaChevronDown, FaChevronUp,
  FaUsers, FaChartBar,
} from "react-icons/fa";
import EmptyFeatureState from "../components/EmptyFeatureState";

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) setMatches(media.matches);
    const listener = () => setMatches(media.matches);
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, [matches, query]);
  return matches;
}

// ── Status badge ──
function StatusBadge({ status }) {
  const map = {
    draft:   { bg: "#f3f4f6", color: "#6b7280", label: "Draft" },
    queued:  { bg: "#fef3c7", color: "#92400e", label: "Queued" },
    sending: { bg: "#dbeafe", color: "#1d4ed8", label: "Sending" },
    sent:    { bg: "#d1fae5", color: "#065f46", label: "Sent" },
    failed:  { bg: "#fee2e2", color: "#991b1b", label: "Failed" },
  };
  const s = map[status] || map.draft;
  return (
    <span style={{
      background: s.bg, color: s.color, fontSize: 11, fontWeight: 600,
      padding: "3px 10px", borderRadius: 999, whiteSpace: "nowrap",
    }}>{s.label}</span>
  );
}

// ── Main component ──
export default function EmailCampaigns() {
  const isMobile = !useMediaQuery("(min-width: 768px)");

  // ── List view state ──
  const [campaigns, setCampaigns] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [deleting, setDeleting] = useState(false);

  // ── View mode: 'list' or 'create' ──
  const [view, setView] = useState("list");
  const [editingCampaign, setEditingCampaign] = useState(null);

  // ── Create / Edit state ──
  const [articles, setArticles] = useState([]);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [selectedArticleSlugs, setSelectedArticleSlugs] = useState([]);
  const [articleDropdownOpen, setArticleDropdownOpen] = useState(false);

  // Listings selection
  const [includeSaleListings, setIncludeSaleListings] = useState(false);
  const [includeSoldListings, setIncludeSoldListings] = useState(false);
  const [saleCount, setSaleCount] = useState(5);
  const [soldCount, setSoldCount] = useState(5);

  // AI prompt & generation
  const [aiPrompt, setAiPrompt] = useState("");
  const [htmlContent, setHtmlContent] = useState("");
  const [subjectLine, setSubjectLine] = useState("");
  const [campaignId, setCampaignId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const progressTimer = useRef(null);
  const streamingHtmlRef = useRef('');

  // Send sample
  const [sampleEmail, setSampleEmail] = useState("");
  const [sendingSample, setSendingSample] = useState(false);
  const [sampleSent, setSampleSent] = useState(false);

  // Save & Queue
  const [saving, setSaving] = useState(false);
  const [queuing, setQueuing] = useState(false);
  const [queued, setQueued] = useState(false);

  // Preview mode
  const [viewMode, setViewMode] = useState("preview");
  const previewRef = useRef(null);

  // Toast
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Contact manager modal (for users without broadcast enabled)
  const [contactModal, setContactModal] = useState(false);

  const articleDropdownRef = useRef(null);

  // Check if current user is allowed to queue broadcasts
  const isBroadcastEnabled = false;

  function showToast(message, type = "success") {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "success" }), 4000);
  }

  // ── Load campaigns list ──
  async function loadCampaigns(silent = false) {
    if (!silent) setLoadingList(true);
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/list-email-campaigns`, {}
      );
      if (res.data?.campaigns) setCampaigns(res.data.campaigns);
    } catch {
      if (!silent) showToast("Failed to load campaigns.", "error");
    }
    if (!silent) setLoadingList(false);
  }

  useEffect(() => { loadCampaigns(); }, []);

  // ── Auto-poll when campaigns are queued or sending ──
  useEffect(() => {
    const hasActive = campaigns.some(c => c.status === "queued" || c.status === "sending");
    if (!hasActive) return;
    const interval = setInterval(() => { loadCampaigns(true); }, 10000);
    return () => clearInterval(interval);
  }, [campaigns]);

  // ── Load articles for the create view ──
  async function loadArticles() {
    setLoadingArticles(true);
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/articles-by-agency`, { view_scope: "mine" }
      );
      setArticles(Array.isArray(res.data) ? res.data : []);
    } catch {
      /* silent */
    }
    setLoadingArticles(false);
  }

  // ── Close dropdown on outside click ──
  useEffect(() => {
    if (!articleDropdownOpen) return;
    function handler(e) {
      if (articleDropdownRef.current && !articleDropdownRef.current.contains(e.target))
        setArticleDropdownOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [articleDropdownOpen]);

  // ── Progress helpers ──
  function startProgress() {
    setProgress(0);
    const startTime = Date.now();
    const DURATION = 60000;
    if (progressTimer.current) clearInterval(progressTimer.current);
    progressTimer.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const ratio = Math.min(elapsed / DURATION, 1);
      setProgress(Math.min(95, ratio * 100 * (2 - ratio)));
    }, 200);
  }
  function stopProgress() {
    if (progressTimer.current) clearInterval(progressTimer.current);
    setProgress(100);
    setTimeout(() => setProgress(0), 500);
  }
  useEffect(() => () => {
    if (progressTimer.current) clearInterval(progressTimer.current);
  }, []);

  // ── Reset create form ──
  function resetCreateForm() {
    setSelectedArticleSlugs([]);
    setIncludeSaleListings(false);
    setIncludeSoldListings(false);
    setSaleCount(5);
    setSoldCount(5);
    setAiPrompt("");
    setHtmlContent("");
    setSubjectLine("");
    setCampaignId(null);
    setQueued(false);
    setSampleEmail("");
    setSampleSent(false);
    setViewMode("preview");
    setEditingCampaign(null);
  }

  // ── Open create view ──
  function handleNewCampaign() {
    resetCreateForm();
    loadArticles();
    setView("create");
  }

  // ── Open edit view for existing campaign ──
  function handleEditCampaign(campaign) {
    resetCreateForm();
    loadArticles();
    setEditingCampaign(campaign);
    setCampaignId(campaign.id);
    setSubjectLine(campaign.subject_line || "");
    setHtmlContent(campaign.html_content || "");
    setAiPrompt(campaign.prompt || "");
    setIncludeSaleListings(campaign.include_sale_listings || false);
    setIncludeSoldListings(campaign.include_sold_listings || false);
    // Load listing selection
    const ls = campaign.listing_selection || {};
    if (ls.sale_count) setSaleCount(ls.sale_count);
    if (ls.sold_count) setSoldCount(ls.sold_count);
    // Load selected article slugs
    const slugs = campaign.article_slugs && campaign.article_slugs.length > 0
      ? campaign.article_slugs : [];
    setSelectedArticleSlugs(slugs);
    setView("create");
  }

  // ── Delete campaign ──
  async function handleDelete() {
    if (!deleteModal.id) return;
    setDeleting(true);
    try {
      await handleTokenAndPostRequest(
        `${window.api_host}/delete-email-campaign`,
        { campaign_id: deleteModal.id }
      );
      setCampaigns(prev => prev.filter(c => c.id !== deleteModal.id));
      showToast("Campaign deleted.");
    } catch {
      showToast("Failed to delete campaign.", "error");
    }
    setDeleting(false);
    setDeleteModal({ open: false, id: null });
  }

  // ── Generate newsletter (streaming) ──
  async function handleGenerate() {
    if (selectedArticleSlugs.length === 0) {
      showToast("Select at least one article.", "error");
      return;
    }
    setGenerating(true);
    startProgress();
    streamingHtmlRef.current = '';
    // Clear preview container for fresh streaming
    if (previewRef.current) {
      previewRef.current.innerHTML = '';
    }

    try {
      const payload = {
        article_slugs: selectedArticleSlugs,
        prompt: aiPrompt,
        existing_html: htmlContent || undefined,
        include_sale_listings: includeSaleListings,
        include_sold_listings: includeSoldListings,
        campaign_id: campaignId || undefined,
        listing_selection: {
          sale_count: includeSaleListings ? saleCount : 0,
          sold_count: includeSoldListings ? soldCount : 0,
        },
      };
      const jwt_token = localStorage.getItem("jwt_token");
      const headers = { 'Content-Type': 'application/json' };
      if (jwt_token) headers['Authorization'] = `Bearer ${jwt_token}`;

      const streamResponse = await fetch(
        `${window.api_host}/generate-email-campaign`,
        { method: 'POST', headers, body: JSON.stringify(payload) }
      );

      if (!streamResponse.ok) {
        showToast("Failed to generate newsletter.", "error");
        stopProgress();
        setGenerating(false);
        return;
      }

      const reader = streamResponse.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop();

        for (const event of events) {
          const dataLine = event.split('\n').find(l => l.startsWith('data: '));
          if (!dataLine) continue;
          try {
            const parsed = JSON.parse(dataLine.slice(6));
            if (parsed.type === 'chunk') {
              streamingHtmlRef.current += parsed.content;
              // Append chunk directly to DOM — no React re-render, no flash
              if (previewRef.current) {
                const frag = document.createRange().createContextualFragment(parsed.content);
                previewRef.current.appendChild(frag);
                previewRef.current.scrollTop = previewRef.current.scrollHeight;
              }
            } else if (parsed.type === 'done') {
              setCampaignId(parsed.campaign_id);
              setSubjectLine(parsed.subject_line || subjectLine);
              setAiPrompt("");
            } else if (parsed.type === 'error') {
              console.error('Stream error:', parsed.message);
              showToast("Failed to generate newsletter.", "error");
            }
          } catch (parseErr) {
            console.warn('Failed to parse SSE event:', dataLine);
          }
        }
      }

      // Final — store full HTML for iframe/save/send
      const finalHtml = streamingHtmlRef.current;
      if (finalHtml) {
        setHtmlContent(finalHtml);
        showToast(htmlContent ? "Newsletter updated." : "Newsletter generated!");
      }
    } catch {
      showToast("Failed to generate newsletter.", "error");
    }
    stopProgress();
    setGenerating(false);
  }

  // ── Send sample ──
  async function handleSendSample() {
    if (!sampleEmail.trim() || !htmlContent) return;
    setSendingSample(true);
    setSampleSent(false);
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/send-email-campaign-sample`,
        { sample_email: sampleEmail, html_content: htmlContent, subject_line: subjectLine, campaign_id: campaignId }
      );
      if (res.data?.status === "success") {
        setSampleSent(true);
        showToast(`Sample sent to ${sampleEmail}`);
        setTimeout(() => setSampleSent(false), 3000);
      } else {
        showToast("Failed to send sample.", "error");
      }
    } catch {
      showToast("Failed to send sample.", "error");
    }
    setSendingSample(false);
  }

  // ── Save campaign (draft) ──
  async function handleSave() {
    setSaving(true);
    try {
      const payload = {
        campaign_id: campaignId || undefined,
        article_slugs: selectedArticleSlugs,
        subject_line: subjectLine,
        html_content: htmlContent,
        prompt: aiPrompt,
        include_sale_listings: includeSaleListings,
        include_sold_listings: includeSoldListings,
        listing_selection: {
          sale_count: includeSaleListings ? saleCount : 0,
          sold_count: includeSoldListings ? soldCount : 0,
        },
      };
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/save-email-campaign`, payload
      );
      if (res.data?.status === "success") {
        setCampaignId(res.data.campaign_id || campaignId);
        showToast("Campaign saved.");
      } else {
        showToast("Failed to save campaign.", "error");
      }
    } catch {
      showToast("Failed to save campaign.", "error");
    }
    setSaving(false);
  }

  // ── Queue broadcast ──
  async function handleQueue() {
    if (!campaignId) return;
    setQueuing(true);
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/queue-email-campaign`,
        { campaign_id: campaignId }
      );
      if (res.data?.status === "success") {
        setQueued(true);
        showToast("Campaign queued for broadcast!");
      } else {
        showToast(res.data?.message || "Failed to queue.", "error");
      }
    } catch {
      showToast("Failed to queue.", "error");
    }
    setQueuing(false);
  }

  // ── Toggle article selection ──
  function toggleArticle(slug) {
    setSelectedArticleSlugs(prev =>
      prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug]
    );
  }

  const hasNewsletter = !!htmlContent || generating;
  const selectedArticles = articles.filter(a => selectedArticleSlugs.includes(a.slug));

  // ────────────────────────── LIST VIEW ──────────────────────────
  if (view === "list") {
    return (
      <div style={{
        flex: 1, overflowY: "auto", padding: isMobile ? "72px 12px 24px" : "32px 40px",
        maxWidth: 900, margin: "0 auto", width: "100%", boxSizing: "border-box",
        display: "flex", flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexWrap: "wrap", gap: 12, marginBottom: 24,
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: isMobile ? 26 : 32, fontWeight: 700, color: "#1f2937" }}>
              AI Email Campaigns
            </h2>
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
              Create, preview, and send AI-generated email newsletters to your contacts.
            </div>
          </div>
          <button
            onClick={handleNewCampaign}
            style={{
              background: "#7c3aed", color: "#fff", border: "none", borderRadius: 8,
              padding: "10px 20px", fontWeight: 600, cursor: "pointer", fontSize: 14,
              display: "flex", alignItems: "center", gap: 8,
            }}
          >
            <FaPlus style={{ fontSize: 12 }} /> New Campaign
          </button>
        </div>

        {/* Campaign list */}
        {loadingList ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {[1, 2, 3].map(i => (
              <div key={i} style={{
                background: "#fff", borderRadius: 16, border: "1px solid #e5e7eb",
                boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                padding: 0, display: "flex",
                flexDirection: isMobile ? "column" : "row",
                overflow: "hidden",
                animation: "skeletonPulse 1.5s ease-in-out infinite",
              }}>
                {/* Thumbnail skeleton */}
                <div style={{
                  width: isMobile ? "100%" : 220,
                  height: isMobile ? 0 : "auto",
                  paddingBottom: isMobile ? "60%" : undefined,
                  minHeight: isMobile ? undefined : 160,
                  flexShrink: 0,
                  background: "#e5e7eb",
                  borderRight: isMobile ? "none" : "1px solid #e5e7eb",
                  borderBottom: isMobile ? "1px solid #e5e7eb" : "none",
                }} />
                {/* Content skeleton */}
                <div style={{ flex: 1, padding: isMobile ? "16px 16px 14px" : "18px 22px", display: "flex", flexDirection: "column", minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <div style={{ width: 180 + i * 20, height: 14, background: "#e5e7eb", borderRadius: 6 }} />
                    <div style={{ width: 48, height: 20, background: "#f3f4f6", borderRadius: 999 }} />
                  </div>
                  <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                    <div style={{ width: 100, height: 10, background: "#f3f4f6", borderRadius: 4 }} />
                    <div style={{ width: 80, height: 10, background: "#f3f4f6", borderRadius: 4 }} />
                  </div>
                  <div style={{ marginTop: "auto", display: "flex", gap: 8 }}>
                    <div style={{ width: 52, height: 28, background: "#f3f4f6", borderRadius: 8 }} />
                    <div style={{ width: 64, height: 28, background: "#f3f4f6", borderRadius: 8 }} />
                  </div>
                </div>
              </div>
            ))}
            <style>{`@keyframes skeletonPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
          </div>
        ) : campaigns.length === 0 ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <EmptyFeatureState
            icon={<FaBroadcastTower />}
            title="No Campaigns Yet"
            description="Create AI-powered email campaigns that combine your articles, market data, and listings into beautiful newsletters — sent to your entire contact list or a targeted segment."
            features={[
              { icon: <FaMagic />, text: "AI writes the email content and designs the layout from your articles and listings" },
              { icon: <FaUsers />, text: "Send to all contacts or filter by suburb, engagement level, or property status" },
              { icon: <FaChartBar />, text: "Track opens, clicks, and engagement — see exactly who read your campaign" },
            ]}
            examplePreview={
              <div style={{ padding: 16 }}>
                <div style={{
                  background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb",
                  overflow: "hidden", maxWidth: 400, margin: "0 auto",
                }}>
                  <div style={{
                    background: "linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)",
                    padding: "20px 24px", color: "#fff",
                  }}>
                    <div style={{ fontSize: 10, opacity: 0.8, letterSpacing: 0.5 }}>NEWSLETTER</div>
                    <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>Spring Market Update — Eastern Suburbs</div>
                  </div>
                  <div style={{ padding: "16px 24px" }}>
                    <div style={{ fontSize: 13, color: "#374151", marginBottom: 12, lineHeight: 1.6 }}>Hi Sarah, here's what's happening in your area this month...</div>
                    <div style={{
                      background: "#f8fafc", borderRadius: 8, padding: 14, marginBottom: 12,
                      border: "1px solid #f3f4f6",
                    }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.3 }}>Featured Article</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#1f2937", marginBottom: 4 }}>Why Coogee Sellers Are Listing Earlier This Spring</div>
                      <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.5 }}>Median days on market dropped to 24 — down 18% from last quarter...</div>
                    </div>
                    <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                      {[{ suburb: "Coogee", median: "$2.1M", change: "+5.3%" }, { suburb: "Bondi", median: "$2.8M", change: "+3.1%" }].map(s => (
                        <div key={s.suburb} style={{
                          flex: 1, background: "#f8fafc", borderRadius: 8, padding: "10px 12px",
                          border: "1px solid #f3f4f6",
                        }}>
                          <div style={{ fontSize: 11, color: "#9ca3af" }}>{s.suburb}</div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: "#1f2937" }}>{s.median}</div>
                          <div style={{ fontSize: 10, color: "#059669" }}>{s.change}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{
                      background: "#2563eb", color: "#fff", borderRadius: 6,
                      padding: "8px 16px", fontSize: 12, fontWeight: 600, textAlign: "center",
                    }}>View Full Market Report</div>
                  </div>
                </div>
              </div>
            }
            ctaLabel="Create Your First Campaign"
            onCta={handleNewCampaign}
            isMobile={isMobile}
          />
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {campaigns.map(c => (
              <div key={c.id} style={{
                background: "#fff", borderRadius: 16, border: "1px solid #e5e7eb",
                boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                padding: 0, display: "flex",
                flexDirection: isMobile ? "column" : "row",
                overflow: "hidden",
                transition: "box-shadow 0.2s, transform 0.15s",
              }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.12)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.08)"; e.currentTarget.style.transform = "none"; }}
              >
                {/* Email preview thumbnail */}
                {c.html_content && (
                  <div style={{
                    width: isMobile ? "100%" : 220,
                    height: isMobile ? 0 : "auto",
                    paddingBottom: isMobile ? "60%" : undefined,
                    minHeight: isMobile ? undefined : 160,
                    flexShrink: 0,
                    position: "relative",
                    overflow: "hidden",
                    background: "#f9fafb",
                    borderRight: isMobile ? "none" : "1px solid #e5e7eb",
                    borderBottom: isMobile ? "1px solid #e5e7eb" : "none",
                  }}>
                    <iframe
                      title={`Preview ${c.id}`}
                      sandbox="allow-same-origin"
                      srcDoc={c.html_content}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: 600,
                        height: 800,
                        border: "none",
                        transformOrigin: "top left",
                        transform: `scale(${(isMobile ? (window.innerWidth - 34) : 220) / 600})`,
                        pointerEvents: "none",
                      }}
                    />
                  </div>
                )}

                {/* Card content */}
                <div style={{ flex: 1, padding: isMobile ? "16px 16px 14px" : "18px 22px", display: "flex", flexDirection: "column", minWidth: 0 }}>
                  <div style={{
                    display: "flex", alignItems: "center", gap: 8,
                    flexWrap: "wrap", marginBottom: 6,
                  }}>
                    <span style={{ fontWeight: 600, fontSize: 14, color: "#1a1a1a" }}>
                      {c.subject_line || c.article_title || "Untitled Campaign"}
                    </span>
                    <StatusBadge status={c.status} />
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
                    {c.article_title && <span>Article: {c.article_title}</span>}
                    {c.created_at && (
                      <span style={{ marginLeft: c.article_title ? 12 : 0 }}>
                        Created {new Date(c.created_at * 1000).toLocaleDateString()}
                      </span>
                    )}
                    {c.recipient_count > 0 && (
                      <span style={{ marginLeft: 12 }}>
                        {c.sent_count}/{c.recipient_count} sent
                      </span>
                    )}
                  </div>
                  <div style={{ marginTop: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {c.status === "draft" && (
                      <>
                        <button
                          onClick={() => handleEditCampaign(c)}
                          style={{
                            background: "#f5f3ff", color: "#6d28d9", border: "1px solid #ddd6fe",
                            borderRadius: 8, padding: "6px 14px", fontWeight: 600,
                            cursor: "pointer", fontSize: 12,
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteModal({ open: true, id: c.id })}
                          style={{
                            background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca",
                            borderRadius: 8, padding: "6px 14px", fontWeight: 600,
                            cursor: "pointer", fontSize: 12, display: "flex",
                            alignItems: "center", gap: 4,
                          }}
                        >
                          <FaTrash style={{ fontSize: 10 }} /> Delete
                        </button>
                      </>
                    )}
                    {(c.status === "sent" || c.status === "queued") && (
                      <button
                        onClick={() => handleEditCampaign(c)}
                        style={{
                          background: "#f9fafb", color: "#374151", border: "1px solid #e5e7eb",
                          borderRadius: 8, padding: "6px 14px", fontWeight: 600,
                          cursor: "pointer", fontSize: 12,
                        }}
                      >
                        View
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Delete confirmation modal */}
        {deleteModal.open && (
          <div onClick={() => setDeleteModal({ open: false, id: null })} style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.35)", zIndex: 2000,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <div onClick={e => e.stopPropagation()} style={{
              background: "#fff", borderRadius: 14, padding: 28, width: 360,
              boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            }}>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12 }}>
                Delete Campaign?
              </div>
              <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 20 }}>
                This cannot be undone.
              </div>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button
                  onClick={() => setDeleteModal({ open: false, id: null })}
                  style={{
                    background: "#f3f4f6", color: "#374151", border: "none",
                    borderRadius: 8, padding: "8px 18px", fontWeight: 600,
                    cursor: "pointer", fontSize: 13,
                  }}
                >Cancel</button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  style={{
                    background: "#dc2626", color: "#fff", border: "none",
                    borderRadius: 8, padding: "8px 18px", fontWeight: 600,
                    cursor: deleting ? "not-allowed" : "pointer", fontSize: 13,
                    display: "flex", alignItems: "center", gap: 6,
                  }}
                >
                  {deleting && <Spinner animation="border" size="sm" style={{ width: 12, height: 12 }} />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast */}
        {toast.show && (
          <div style={{
            position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
            background: toast.type === "error" ? "#dc2626" : "#065f46", color: "#fff",
            padding: "12px 24px", borderRadius: 8, fontSize: 14, fontWeight: 600,
            boxShadow: "0 4px 16px rgba(0,0,0,0.18)", zIndex: 2100,
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <span style={{ fontSize: 16 }}>{toast.type === "error" ? "✕" : "✓"}</span> {toast.message}
          </div>
        )}
      </div>
    );
  }

  // ────────────────────────── CREATE / EDIT VIEW ──────────────────────────
  return (
    <div style={{
      flex: 1, display: "flex", flexDirection: "column", overflow: "hidden",
      marginTop: isMobile ? 56 : 0,
    }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: isMobile ? "12px 16px" : "16px 24px",
        borderBottom: "1px solid #e5e7eb", background: "#fff", flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            onClick={() => { setView("list"); loadCampaigns(); }}
            style={{
              background: "none", border: "none", cursor: "pointer",
              fontSize: 16, color: "#6b7280", padding: "0 4px", lineHeight: 1,
            }}
          >
            <FaArrowLeft />
          </button>
          <FaBroadcastTower style={{ fontSize: 18, color: "#7c3aed" }} />
          <span style={{ fontWeight: 700, fontSize: isMobile ? 15 : 18, color: "#1a1a1a" }}>
            {editingCampaign ? "Edit Campaign" : "New Email Campaign"}
          </span>
        </div>
        <button
          onClick={() => { setView("list"); loadCampaigns(); }}
          disabled={generating}
          style={{
            background: "none", border: "none", cursor: "pointer",
            fontSize: 20, color: "#9ca3af", padding: "0 4px", lineHeight: 1,
          }}
        ><FaTimes /></button>
      </div>

      {/* Progress bar */}
      {generating && (
        <div style={{ padding: "0 24px", flexShrink: 0, background: "#fff" }}>
          <div style={{ width: "100%", height: 4, background: "#e5e7eb", borderRadius: 2, overflow: "hidden" }}>
            <div style={{
              height: "100%", width: `${progress}%`,
              background: "linear-gradient(90deg, #7c3aed, #a78bfa)", borderRadius: 2,
              transition: progress === 100 ? "width 0.3s ease" : "width 0.4s linear",
            }} />
          </div>
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4, marginBottom: 8, textAlign: "center" }}>
            {progress < 95 ? "Generating your newsletter — this usually takes about a minute…" : "Almost there…"}
          </div>
        </div>
      )}

      {/* Body */}
      <div style={{
        flex: 1, overflow: "hidden", display: "flex",
        flexDirection: isMobile ? "column" : "row", minHeight: 0,
        overflowY: isMobile ? "auto" : "hidden",
      }}>
        {/* ── Left panel: Controls ── */}
        <div style={{
          width: isMobile ? "100%" : 400, minWidth: isMobile ? undefined : 400, flexShrink: 0,
          background: "#fff", borderRight: isMobile ? "none" : "1px solid #e5e7eb",
          borderBottom: isMobile ? "1px solid #e5e7eb" : "none",
          overflowY: isMobile ? "visible" : "auto", padding: isMobile ? "16px" : "20px 24px",
          display: "flex", flexDirection: "column", gap: 20,
        }}>

          {/* ── Article Selection ── */}
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
              Select Articles
            </div>
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 8, lineHeight: 1.4 }}>
              Choose one or more published articles to feature in this campaign.
            </div>

            {/* Selected article pills */}
            {selectedArticles.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                {selectedArticles.map(a => (
                  <span key={a.slug} style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    background: "#ede9fe", color: "#6d28d9", fontSize: 12,
                    padding: "4px 10px", borderRadius: 999, fontWeight: 500,
                  }}>
                    {a.title?.length > 30 ? a.title.slice(0, 30) + "…" : a.title}
                    <FaTimes
                      onClick={() => toggleArticle(a.slug)}
                      style={{ cursor: "pointer", fontSize: 10 }}
                    />
                  </span>
                ))}
              </div>
            )}

            {/* Dropdown trigger */}
            <div ref={articleDropdownRef} style={{ position: "relative" }}>
              <button
                onClick={() => setArticleDropdownOpen(v => !v)}
                disabled={loadingArticles}
                style={{
                  width: "100%", display: "flex", alignItems: "center",
                  justifyContent: "space-between", padding: "10px 12px",
                  border: "1px solid #e5e7eb", borderRadius: 8, background: "#f9fafb",
                  cursor: "pointer", fontSize: 13, color: "#374151",
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <FaNewspaper style={{ fontSize: 12, color: "#9ca3af" }} />
                  {loadingArticles ? "Loading articles…"
                    : selectedArticleSlugs.length === 0 ? "Choose articles…"
                    : `${selectedArticleSlugs.length} selected`}
                </span>
                {articleDropdownOpen ? <FaChevronUp style={{ fontSize: 10 }} /> : <FaChevronDown style={{ fontSize: 10 }} />}
              </button>

              {articleDropdownOpen && (
                <div style={{
                  position: "absolute", top: "100%", left: 0, right: 0,
                  background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.1)", zIndex: 100,
                  maxHeight: 260, overflowY: "auto", marginTop: 4,
                }}>
                  {articles.filter(a => a.is_live).length === 0 ? (
                    <div style={{ padding: 16, fontSize: 13, color: "#9ca3af", textAlign: "center" }}>
                      No published articles found.
                    </div>
                  ) : (
                    articles.filter(a => a.is_live).map(a => {
                      const selected = selectedArticleSlugs.includes(a.slug);
                      return (
                        <label key={a.slug} style={{
                          display: "flex", alignItems: "center", gap: 10,
                          padding: "10px 14px", cursor: "pointer", fontSize: 13,
                          background: selected ? "#f5f3ff" : "transparent",
                          borderBottom: "1px solid #f3f4f6",
                        }}>
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleArticle(a.slug)}
                            style={{ accentColor: "#7c3aed", width: 15, height: 15, flexShrink: 0 }}
                          />
                          <div style={{ minWidth: 0 }}>
                            <div style={{
                              fontWeight: 500, color: "#1a1a1a",
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>{a.title}</div>
                            <div style={{ fontSize: 11, color: "#9ca3af" }}>{a.author}</div>
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── Listings Selection ── */}
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
              Include Listings
            </div>

            {/* Sale listings */}
            <label style={{
              display: "flex", alignItems: "center", gap: 8, fontSize: 13,
              color: "#374151", cursor: "pointer", marginBottom: 8,
              padding: "8px 10px", borderRadius: 8,
              background: includeSaleListings ? "#f0fdf4" : "#f9fafb",
              border: includeSaleListings ? "1px solid #86efac" : "1px solid #e5e7eb",
              transition: "all 0.15s",
            }}>
              <input
                type="checkbox" checked={includeSaleListings}
                onChange={e => setIncludeSaleListings(e.target.checked)}
                style={{ accentColor: "#22c55e", width: 16, height: 16 }}
              />
              <span style={{ flex: 1 }}>
                <strong>Live Sale Listings</strong><br />
                <span style={{ fontSize: 11, color: "#6b7280" }}>Current properties for sale</span>
              </span>
            </label>
            {includeSaleListings && (
              <div style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "6px 10px 10px 36px", marginBottom: 4,
              }}>
                <span style={{ fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>Most recent</span>
                <select
                  value={saleCount}
                  onChange={e => setSaleCount(Number(e.target.value))}
                  style={{
                    padding: "4px 8px", borderRadius: 6, border: "1px solid #e5e7eb",
                    fontSize: 13, background: "#fff", cursor: "pointer",
                  }}
                >
                  {[1,2,3,4,5,6,8,10].map(n =>
                    <option key={n} value={n}>{n}</option>
                  )}
                </select>
                <span style={{ fontSize: 12, color: "#6b7280" }}>listings</span>
              </div>
            )}

            {/* Sold listings */}
            <label style={{
              display: "flex", alignItems: "center", gap: 8, fontSize: 13,
              color: "#374151", cursor: "pointer",
              padding: "8px 10px", borderRadius: 8,
              background: includeSoldListings ? "#f0fdf4" : "#f9fafb",
              border: includeSoldListings ? "1px solid #86efac" : "1px solid #e5e7eb",
              transition: "all 0.15s",
            }}>
              <input
                type="checkbox" checked={includeSoldListings}
                onChange={e => setIncludeSoldListings(e.target.checked)}
                style={{ accentColor: "#22c55e", width: 16, height: 16 }}
              />
              <span style={{ flex: 1 }}>
                <strong>Sold Properties</strong><br />
                <span style={{ fontSize: 11, color: "#6b7280" }}>Recent sold results</span>
              </span>
            </label>
            {includeSoldListings && (
              <div style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "6px 10px 10px 36px",
              }}>
                <span style={{ fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>Most recent</span>
                <select
                  value={soldCount}
                  onChange={e => setSoldCount(Number(e.target.value))}
                  style={{
                    padding: "4px 8px", borderRadius: 6, border: "1px solid #e5e7eb",
                    fontSize: 13, background: "#fff", cursor: "pointer",
                  }}
                >
                  {[1,2,3,4,5,6,8,10].map(n =>
                    <option key={n} value={n}>{n}</option>
                  )}
                </select>
                <span style={{ fontSize: 12, color: "#6b7280" }}>listings</span>
              </div>
            )}
          </div>

          {/* ── AI Prompt ── */}
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
              {hasNewsletter ? "Edit with AI" : "Generate Newsletter"}
            </div>
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 8, lineHeight: 1.4 }}>
              {hasNewsletter
                ? "Describe changes you'd like the AI to make to your newsletter."
                : "Optionally add instructions for the AI, or click Generate to create your newsletter."}
            </div>
            {/* Quick prompt pills */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
              {(hasNewsletter
                ? [
                    { label: "✍️ More Professional", p: "Make the tone more professional and authoritative." },
                    { label: "🎨 Change Layout", p: "Try a different layout structure while keeping the same content." },
                    { label: "✂️ Make Shorter", p: "Make the newsletter more concise." },
                    { label: "📣 Stronger CTA", p: "Make the call-to-action more compelling and prominent." },
                  ]
                : [
                    { label: "🏡 Market Insight", p: "Focus on local market trends and what they mean for homeowners in the area." },
                    { label: "💰 Investment Angle", p: "Highlight the investment opportunity and recent price growth in the area." },
                    { label: "📊 Data Driven", p: "Emphasise key statistics and data points from the article." },
                    { label: "🤝 Personal Touch", p: "Make it warm and personal — like a note from a trusted local expert." },
                    { label: "⚡ Urgency", p: "Create a sense of urgency — now is the time to act on property decisions." },
                  ]
              ).map((ex, i) => (
                <button
                  key={i}
                  onClick={() => setAiPrompt(ex.p)}
                  disabled={generating}
                  style={{
                    background: aiPrompt === ex.p ? "#ede9fe" : "#f3f4f6",
                    color: aiPrompt === ex.p ? "#6d28d9" : "#374151",
                    border: aiPrompt === ex.p ? "1px solid #c4b5fd" : "1px solid #e5e7eb",
                    borderRadius: 999, padding: "4px 12px", fontSize: 12,
                    fontWeight: 500, cursor: generating ? "not-allowed" : "pointer",
                    whiteSpace: "nowrap", transition: "all 0.15s",
                  }}
                >{ex.label}</button>
              ))}
            </div>
            <textarea
              value={aiPrompt}
              onChange={e => setAiPrompt(e.target.value)}
              placeholder={hasNewsletter
                ? "e.g. Make the header more eye-catching, add a testimonial section…"
                : "e.g. Focus on the investment opportunity angle, highlight recent price growth…"}
              disabled={generating}
              rows={3}
              style={{
                width: "100%", padding: "10px 12px", borderRadius: 8,
                border: "1px solid #e5e7eb", fontSize: 13, boxSizing: "border-box",
                resize: "vertical", fontFamily: "inherit", marginBottom: 8,
              }}
            />
            <button
              onClick={handleGenerate}
              disabled={generating || selectedArticleSlugs.length === 0}
              style={{
                background: generating ? "#a78bfa" : "#7c3aed",
                color: "#fff", border: "none", borderRadius: 8,
                padding: "10px 20px", fontWeight: 600,
                cursor: generating || selectedArticleSlugs.length === 0 ? "not-allowed" : "pointer",
                fontSize: 14, display: "flex", alignItems: "center",
                justifyContent: "center", width: "100%", gap: 8,
                opacity: selectedArticleSlugs.length === 0 ? 0.5 : 1,
              }}
            >
              {generating
                ? <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
                : <FaMagic style={{ fontSize: 13 }} />}
              {generating ? "Generating…" : hasNewsletter ? "Apply AI Edit" : "Generate Newsletter"}
            </button>
          </div>

          {/* ── Subject line ── */}
          {hasNewsletter && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
                Subject Line
              </div>
              <input
                value={subjectLine}
                onChange={e => setSubjectLine(e.target.value)}
                style={{
                  width: "100%", padding: "10px 12px", borderRadius: 8,
                  border: "1px solid #e5e7eb", fontSize: 13, boxSizing: "border-box",
                }}
              />
            </div>
          )}

          {/* ── Send sample ── */}
          {hasNewsletter && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
                Send Sample
              </div>
              <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 8, lineHeight: 1.4 }}>
                Send a preview to your email to see how it looks in your inbox.
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ flex: 1, position: "relative" }}>
                  <FaEnvelope style={{
                    position: "absolute", left: 10, top: "50%",
                    transform: "translateY(-50%)", color: "#9ca3af", fontSize: 13,
                  }} />
                  <input
                    value={sampleEmail}
                    onChange={e => { setSampleEmail(e.target.value); setSampleSent(false); }}
                    placeholder="email@example.com" type="email"
                    style={{
                      width: "100%", padding: "10px 12px 10px 32px", borderRadius: 8,
                      border: "1px solid #e5e7eb", fontSize: 13, boxSizing: "border-box",
                    }}
                  />
                </div>
                <button
                  onClick={handleSendSample}
                  disabled={sendingSample || !sampleEmail.trim() || !htmlContent}
                  style={{
                    background: sampleSent ? "#059669" : "#2563eb",
                    color: "#fff", border: "none", borderRadius: 8,
                    padding: "10px 16px", fontWeight: 600,
                    cursor: sendingSample || !sampleEmail.trim() ? "not-allowed" : "pointer",
                    fontSize: 13, display: "flex", alignItems: "center",
                    gap: 6, whiteSpace: "nowrap",
                    opacity: !sampleEmail.trim() ? 0.5 : 1, transition: "background 0.2s",
                  }}
                >
                  {sendingSample
                    ? <Spinner animation="border" size="sm" style={{ width: 13, height: 13 }} />
                    : sampleSent ? <FaCheckCircle style={{ fontSize: 13 }} />
                    : <FaPaperPlane style={{ fontSize: 12 }} />}
                  {sampleSent ? "Sent!" : "Send"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Right panel: Preview ── */}
        <div style={{
          flex: 1, overflow: "hidden", display: "flex", flexDirection: "column",
          alignItems: "center", padding: isMobile ? 16 : 24, background: "#f1f5f9",
          minHeight: isMobile ? 400 : 0,
        }}>
          {!hasNewsletter ? (
            <div style={{
              display: "flex", flexDirection: "column", alignItems: "center",
              justifyContent: "center", flex: 1, color: "#9ca3af",
              textAlign: "center", gap: 12, maxWidth: 360,
            }}>
              <FaBroadcastTower style={{ fontSize: 40, color: "#d1d5db" }} />
              <div style={{ fontSize: 16, fontWeight: 600, color: "#6b7280" }}>
                Newsletter Preview
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                Generate your newsletter using the controls on the left. The AI
                will create a professional email using your branding, article
                content, and any listings you choose to include.
              </div>
            </div>
          ) : (
            <>
              {/* Preview/Raw toggle */}
              <div style={{
                display: "flex", gap: 0, marginBottom: 16, background: "#f1f5f9",
                borderRadius: 8, overflow: "hidden", width: "fit-content",
                border: "1px solid #e5e7eb", flexShrink: 0,
              }}>
                <button
                  onClick={() => setViewMode("preview")}
                  style={{
                    background: viewMode === "preview" ? "#fff" : "transparent",
                    color: viewMode === "preview" ? "#7c3aed" : "#6b7280",
                    border: "none", borderRadius: viewMode === "preview" ? 7 : 0,
                    padding: "8px 20px", fontWeight: 600, cursor: "pointer",
                    fontSize: 13, display: "flex", alignItems: "center", gap: 6,
                    boxShadow: viewMode === "preview" ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                  }}
                ><FaEye style={{ fontSize: 12 }} /> Preview</button>
                <button
                  onClick={() => setViewMode("compose")}
                  style={{
                    background: viewMode === "compose" ? "#fff" : "transparent",
                    color: viewMode === "compose" ? "#7c3aed" : "#6b7280",
                    border: "none", borderRadius: viewMode === "compose" ? 7 : 0,
                    padding: "8px 20px", fontWeight: 600, cursor: "pointer",
                    fontSize: 13,
                    boxShadow: viewMode === "compose" ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                  }}
                >Raw HTML</button>
              </div>

              {viewMode === "preview" ? (
                <div style={{
                  background: "#fff", borderRadius: 12,
                  boxShadow: "0 2px 16px rgba(0,0,0,0.08)", maxWidth: 640,
                  width: "100%", flex: 1, minHeight: 0,
                  display: "flex", flexDirection: "column", overflow: "hidden",
                }}>
                  <div style={{
                    padding: "14px 20px", borderBottom: "1px solid #f3f4f6",
                    background: "#fafbfc", flexShrink: 0,
                  }}>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 2 }}>Subject</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a" }}>
                      {subjectLine || (generating ? "Generating…" : "")}
                    </div>
                  </div>
                  {generating ? (
                    <div
                      ref={previewRef}
                      style={{
                        flex: 1, overflowY: "auto", padding: 16,
                        fontSize: 14, lineHeight: 1.6, minHeight: 0,
                      }}
                    />
                  ) : (
                    <iframe
                      title="Newsletter preview"
                      srcDoc={htmlContent}
                      sandbox="allow-same-origin"
                      style={{ width: "100%", flex: 1, border: "none", display: "block", minHeight: 0 }}
                    />
                  )}
                </div>
              ) : (
                <textarea
                  value={htmlContent}
                  onChange={e => setHtmlContent(e.target.value)}
                  style={{
                    width: "100%", maxWidth: 640, minHeight: 400, flex: 1,
                    padding: 16, borderRadius: 8, border: "1px solid #e5e7eb",
                    fontSize: 12, fontFamily: "monospace", resize: "vertical",
                    boxSizing: "border-box", background: "#fff",
                  }}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      {hasNewsletter && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "flex-end",
          padding: "12px 24px", borderTop: "1px solid #e5e7eb",
          background: "#fff", flexShrink: 0, gap: 12,
          flexWrap: isMobile ? "wrap" : "nowrap",
        }}>
          {!isMobile && (
            <button
              onClick={() => { setView("list"); loadCampaigns(); }}
              style={{
                background: "#f1f5f9", color: "#374151", border: "none",
                borderRadius: 8, padding: "10px 20px", fontWeight: 600,
                cursor: "pointer", fontSize: 14,
              }}
            >Back to List</button>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              background: "#2563eb", color: "#fff", border: "none",
              borderRadius: 8, padding: "10px 20px", fontWeight: 600,
              cursor: saving ? "not-allowed" : "pointer", fontSize: 14,
              display: "flex", alignItems: "center", gap: 8,
              flex: isMobile ? 1 : undefined,
              justifyContent: "center",
            }}
          >
            {saving
              ? <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
              : null}
            Save Draft
          </button>
          <button
            onClick={() => isBroadcastEnabled ? handleQueue() : setContactModal(true)}
            disabled={queuing || queued}
            style={{
              background: queued ? "#059669" : "#7c3aed",
              color: "#fff", border: "none", borderRadius: 8,
              padding: "10px 24px", fontWeight: 600,
              cursor: queuing || queued ? "not-allowed" : "pointer",
              fontSize: 14, display: "flex", alignItems: "center", gap: 8,
              flex: isMobile ? 1 : undefined,
              justifyContent: "center",
            }}
          >
            {queuing
              ? <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
              : queued ? <FaCheckCircle style={{ fontSize: 14 }} />
              : <FaBroadcastTower style={{ fontSize: 14 }} />}
            {queuing ? "Queuing…" : queued ? "Queued!" : "Queue Broadcast"}
          </button>
        </div>
      )}

      {/* Contact Manager Modal */}
      {contactModal && (
        <div
          onClick={() => setContactModal(false)}
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.35)", zIndex: 2000,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 16, padding: "32px 28px", width: 400,
              maxWidth: "90vw", boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 36, marginBottom: 12 }}>📬</div>
            <div style={{ fontWeight: 700, fontSize: 18, color: "#1a1a1a", marginBottom: 8 }}>
              Enable Email Broadcasts
            </div>
            <div style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.6, marginBottom: 20 }}>
              Contact your account manager to enable email broadcasting for your account.
            </div>
            <div style={{ fontSize: 14, color: "#374151", fontWeight: 600, marginBottom: 20 }}>
              <a href="mailto:bayan@vendorleads.com.au" style={{ color: "#7c3aed", textDecoration: "none" }}>
                bayan@vendorleads.com.au
              </a>
            </div>
            <button
              onClick={() => setContactModal(false)}
              style={{
                background: "#7c3aed", color: "#fff", border: "none",
                borderRadius: 8, padding: "10px 28px", fontWeight: 600,
                cursor: "pointer", fontSize: 14,
              }}
            >Got it</button>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast.show && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: toast.type === "error" ? "#dc2626" : "#065f46", color: "#fff",
          padding: "12px 24px", borderRadius: 8, fontSize: 14, fontWeight: 600,
          boxShadow: "0 4px 16px rgba(0,0,0,0.18)", zIndex: 2100,
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ fontSize: 16 }}>{toast.type === "error" ? "✕" : "✓"}</span> {toast.message}
        </div>
      )}
    </div>
  );
}
